import React from 'react'

import { StyleSheet, View, Keyboard, Image, Platform } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { useFormik } from 'formik'
import * as Yup from 'yup'

import client from '@sart/client'
import { useContent } from '@sart/content'
import { prettyErrorMessage, testID } from '@sart/utilities'
import { adaptive, backgroundColors, typography, colors } from '@sart/styles'

import {
  Title,
  Description,
  Space,
  Button,
  TextInputExtended,
  KeyboardAvoidingView,
  ReturnFooter
} from '@sart/components'

import NeedHelpFooter from '../NeedHelpFooter'
import * as theme from './signTheme'

const passwordRE = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9])(?=\S+$).{12,}$/

export default function SignUpSetPassword(props) {
  const content = useContent()

  const { navigation, route } = props
  const userId = route.params?.userId
  const emailAddress = route.params?.emailAddress

  const formik = useFormik({
    initialValues: {
      email: ''
    },
    validationSchema: Yup.object().shape({
      password: Yup.string()
        .matches(passwordRE, content.set_password.passwordRequirementsError)
        .required(content.forms.required),
      confirmedPassword: Yup.string().required(content.forms.required)
    }),
    onSubmit: handleUpdatePassword
  })

  async function handleUpdatePassword(values, form) {
    //close the keyboard if it is open
    Keyboard.dismiss()

    if (values.password === values.confirmedPassword) {
      try {
        console.log('Updating password for userId: ' + userId)
        const encodedPassword = client.encodeCredentials(values.password)
        await client.callApi('user','modifyUserPassword',[userId, encodedPassword])
        await client.signInWithPasswordAndEmail({
          email: emailAddress,
          password: values.password
        })

        navigation.navigate('Main')
      } catch (error) {
        if (error.response && error.response.body &&
          (error.response.body.messageCode === "SR00151" || error.response.body.code === 'REGISTRATION_INCOMPLETE')) {
            console.log('Registration incomplete - redirecting to complete registration', JSON.stringify(error,null,2))
          //if reg is not completed, redirect to the register details screen
          await handleMustCompleteRegistration(values, form)

        } else {
          form.setFieldError(
            'confirmedPassword',
            prettyErrorMessage(error, 'forgottenPassword', content)
          )
          form.setSubmitting(false)
        }
      }
    } else {
      form.setFieldError(
        'confirmedPassword',
        content.forgotten_password_new_password.validateErrorMessage
      )
      form.setSubmitting(false)
    }
  }

  async function handleMustCompleteRegistration(values, form){
   
    const user = await client.callApi('user','getUser',[userId])

    const params = {
      userId: client.cache.userId,
      customerId: client.cache.customerId,
      // number: user.capturedRegistrationDetails.twoFactorAuthNumber,
      email: user.userName
    }
    navigation.navigate('RegisterDetails', params)
  }

  async function handleMustChangePassword(){
    const auth = await client.callApi('authentication', 'createToken')
    await client.storeAuthInformation(auth)
    await client.cacheAuthInformation(auth)
    client.setupHeaderBearerAuth(client.cache.token)
    
    const user = await client.callApi('user','getUser',[auth.userId])

    const params = {
      ...auth,
      userId: auth.userId,
      emailAddress: user.userName
    }
    navigation.navigate('ForgottenPasswordNewPassword', params)
  }

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView style={styles.content}>
        <View style={styles.logo}>
          <Image
              source={{ uri: 'https://shop.bikermobile.com/assets/biker_mobile_logo_final_nobg.png' }}
              style={styles.logoImage}
          />
          {/* <CompanyIcon color={colors.white} height={styles.logoStyle.height} width={200} /> */}
        </View>

        <Space height={36} />
        <Title {...theme.title}>{content.forgotten_password_new_password.title}</Title>

        <Space height={24} />

        <Description {...theme.description}>
          {content.forgotten_password_new_password.instructions}
        </Description>

        <Space height={24} />

        <TextInputExtended
          allowShowHidePassword={true}
          label={content.set_password.passwordLabel}
          autoCompleteType="password"
          value={formik.values.password}
          placeholder={content.set_password.passwordLabel}
          onChangeText={formik.handleChange('password')}
          onBlur={formik.handleBlur('password')}
          error={formik.touched.password && formik.errors.password}
          {...theme.textInput}
          returnKeyType="next"
          onSubmitEditing={() => this.newPasswordRef.focus()}
          {...testID('forgottenPasswordField')}
        />

        <Space height={8} />

        <TextInputExtended
          allowShowHidePassword={true}
          label={content.set_password.passwordConfirmLabel}
          autoCompleteType="password"
          value={formik.values.confirmedPassword}
          placeholder={content.set_password.passwordConfirmPlaceholder}
          onChangeText={formik.handleChange('confirmedPassword')}
          onBlur={formik.handleBlur('confirmedPassword')}
          error={formik.touched.confirmedPassword && formik.errors.confirmedPassword}
          {...theme.textInput}
          // ref={ref => {
          //   this.newPasswordRef = ref;
          // }}
          returnKeyType="go"
          onSubmitEditing={formik.handleSubmit}
          {...testID('forgottenPasswordFieldConfirmation')}
        />

        <Space height={48} />

        <Button
          title={
            formik.isSubmitting
              ? content.common.submitting
              : content.common.submit
          }
          disabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
          onPress={formik.handleSubmit}
          {...theme.button}
          titleStyle={{ ...typography.extrabold, fontSize: 18, color: colors.white }}
          disabledTitleStyle={{...typography.extrabold, fontSize: 18, color: colors.white + 45}}
          {...testID('forgottenPasswordChangeButton')}
          />
      </KeyboardAvoidingView>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: backgroundColors.darkCharcoal,
    ...Platform.select({
      web: {
        alignItems: 'center'
      }
    })
  },
  content: {
    flex: 1,
    ...adaptive.wrapperPadding,
    //justifyContent: 'center',
    maxWidth: 600,
    width: '100%'
  },
  description: {
    textAlign: 'center'
  },
  cancelStyle: {
    color: colors.white
  },
  logo: {
    alignSelf:"center"
  },
  logoStyle: {
    ...adaptive.guruSize
  },
  logoImage: {
    height: 45,
    width: 45,
    position: 'relative',
    alignSelf: 'center',
    width: 200,
    ...adaptive.guruSize
  },
})
